/**
 * Specialty-contextual field definitions for the Individual Provider
 * dashboard (tracker Phase 9 item 9.9). Deliberately NOT a separate page
 * per specialization - `IndividualProvider.specialization` is a real FK
 * into `uzima_institutions.PartnerSpecializations` with 39 distinct real
 * values seeded in production (confirmed via a live shell query), and
 * `ProviderMedicalRecord.vitals` is already a generic JSONField that
 * accepts any key/value pairs the frontend sends - no backend change is
 * needed to store specialty-specific exam findings, only a frontend
 * mapping from specialization name -> which extra structured fields to
 * show on the existing Clinical Records and Appointments pages, per the
 * scope this item was given ("contextualize existing pages by
 * specialization rather than building separate per-specialty modules").
 *
 * Every one of the 39 real specialization names below was read directly
 * off production (`PartnerSpecializations.objects.values_list('name')`),
 * not guessed - a specialization not in this map (there shouldn't be any,
 * but a taxonomy row could be added later) falls back to no extra fields,
 * which is a safe no-op rather than a crash.
 */

export type SpecialtyField = { key: string; label: string; placeholder?: string };

const FAMILY_FIELDS: Record<string, SpecialtyField[]> = {
    cardiology: [
        { key: 'heart_rhythm', label: 'Heart Rhythm', placeholder: 'e.g. Regular, AFib' },
        { key: 'murmur', label: 'Murmur Present', placeholder: 'e.g. None / Systolic grade II' },
        { key: 'nyha_class', label: 'NYHA Class', placeholder: 'I - IV' },
    ],
    obgyn: [
        { key: 'gravida_para', label: 'Gravida / Para', placeholder: 'e.g. G2P1' },
        { key: 'lmp', label: 'Last Menstrual Period', placeholder: 'YYYY-MM-DD' },
        { key: 'gestational_age', label: 'Gestational Age', placeholder: 'e.g. 28 weeks' },
        { key: 'fetal_heart_rate', label: 'Fetal Heart Rate (bpm)' },
    ],
    dental: [
        { key: 'tooth_quadrant', label: 'Tooth / Quadrant', placeholder: 'e.g. UR6, LL Quadrant' },
        { key: 'occlusion', label: 'Occlusion Type', placeholder: 'e.g. Class I' },
        { key: 'oral_hygiene', label: 'Oral Hygiene Status', placeholder: 'e.g. Good / Fair / Poor' },
    ],
    psychiatry: [
        { key: 'mood', label: 'Mood' },
        { key: 'affect', label: 'Affect' },
        { key: 'risk_assessment', label: 'Risk Assessment', placeholder: 'e.g. No SI/HI reported' },
    ],
    dermatology: [
        { key: 'skin_type', label: 'Skin Type (Fitzpatrick)', placeholder: 'I - VI' },
        { key: 'lesion_description', label: 'Lesion Description' },
        { key: 'lesion_location', label: 'Lesion Location' },
    ],
    ophthalmology: [
        { key: 'visual_acuity_od', label: 'Visual Acuity (OD - right eye)', placeholder: 'e.g. 20/20' },
        { key: 'visual_acuity_os', label: 'Visual Acuity (OS - left eye)', placeholder: 'e.g. 20/25' },
        { key: 'iop', label: 'Intraocular Pressure (mmHg)' },
    ],
    ent: [
        { key: 'hearing_test', label: 'Hearing Test Result' },
        { key: 'tympanic_membrane', label: 'Tympanic Membrane Finding' },
    ],
    orthopedics: [
        { key: 'affected_side', label: 'Affected Limb / Side' },
        { key: 'range_of_motion', label: 'Range of Motion' },
        { key: 'surgical_site', label: 'Surgical Site Status', placeholder: 'e.g. Healing well, no signs of infection' },
    ],
    endocrine: [
        { key: 'blood_glucose', label: 'Blood Glucose (mg/dL)' },
        { key: 'hba1c', label: 'HbA1c (%)' },
        { key: 'insulin_regimen', label: 'Insulin / Medication Regimen' },
    ],
    nephrology: [
        { key: 'egfr', label: 'eGFR (mL/min/1.73m²)' },
        { key: 'dialysis_status', label: 'Dialysis Status', placeholder: 'e.g. Not on dialysis' },
    ],
    gastro: [
        { key: 'abdominal_exam', label: 'Abdominal Exam Findings' },
        { key: 'liver_function_notes', label: 'Liver Function Notes' },
    ],
    oncology: [
        { key: 'cancer_stage', label: 'Stage' },
        { key: 'treatment_regimen', label: 'Treatment Regimen' },
        { key: 'performance_status', label: 'Performance Status (ECOG)', placeholder: '0 - 4' },
    ],
    neurology: [
        { key: 'gcs', label: 'Glasgow Coma Scale' },
        { key: 'neuro_exam', label: 'Neurological Exam Findings' },
    ],
    urology: [
        { key: 'urinalysis', label: 'Urinalysis Findings' },
        { key: 'psa', label: 'PSA (ng/mL)' },
    ],
    neonatal: [
        { key: 'gestational_age_at_birth', label: 'Gestational Age at Birth' },
        { key: 'birth_weight', label: 'Birth Weight (kg)' },
        { key: 'apgar_score', label: 'APGAR Score' },
    ],
    anesthesia_pain: [
        { key: 'pain_score', label: 'Pain Score (0-10)' },
        { key: 'asa_class', label: 'ASA Physical Status Class', placeholder: 'I - VI' },
    ],
    emergency: [
        { key: 'triage_category', label: 'Triage Category' },
        { key: 'gcs', label: 'Glasgow Coma Scale' },
    ],
};

/** Maps each real production specialization name to a family key above. Not present here => no extra fields (safe default). */
const SPECIALIZATION_TO_FAMILY: Record<string, keyof typeof FAMILY_FIELDS> = {
    'Interventional Cardiologist': 'cardiology',
    'Pediatric Cardiologist': 'cardiology',
    'Gynaecologist': 'obgyn',
    'Obstetrician': 'obgyn',
    'Orthodontist': 'dental',
    'Oral & Maxillofacial Surgeon': 'dental',
    'Psychiatrist': 'psychiatry',
    'Child & Adolescent Psychiatrist': 'psychiatry',
    'Cosmetic Dermatologist': 'dermatology',
    'Dermatologist': 'dermatology',
    'Cornea Specialist': 'ophthalmology',
    'Retina Specialist': 'ophthalmology',
    'Audiologist': 'ent',
    'ENT Surgeon (Rhinology)': 'ent',
    'General Surgeon': 'orthopedics',
    'Neurosurgeon': 'orthopedics',
    'Orthopedic Surgeon': 'orthopedics',
    'Trauma Orthopedic Surgeon': 'orthopedics',
    'Sports Medicine Physician': 'orthopedics',
    'Endocrinologist': 'endocrine',
    'Diabetologist': 'endocrine',
    'Nephrologist': 'nephrology',
    'Gastroenterologist': 'gastro',
    'Hepatologist': 'gastro',
    'Oncologist': 'oncology',
    'Radiation Oncologist': 'oncology',
    'Neurologist': 'neurology',
    'Urologist': 'urology',
    'Neonatologist': 'neonatal',
    'Anesthesiologist': 'anesthesia_pain',
    'Pain Management Specialist': 'anesthesia_pain',
    'Emergency Medicine Physician': 'emergency',
    // Family Medicine Physician, General Practitioner, Internal Medicine
    // Physician, Public Health Physician, Pathologist, Radiologist: broad/
    // diagnostic specialties with no single extra-field set that fits -
    // the generic BP/HR/Temp/Weight vitals already on the form are enough.
};

export const getSpecialtyExtraFields = (specialization: string | undefined | null): SpecialtyField[] => {
    if (!specialization) return [];
    const family = SPECIALIZATION_TO_FAMILY[specialization];
    return family ? FAMILY_FIELDS[family] : [];
};

/** key -> label across every family, for display of a saved record whose fields don't depend on the current specialty lookup. */
export const SPECIALTY_FIELD_LABELS: Record<string, string> = Object.values(FAMILY_FIELDS)
    .flat()
    .reduce((acc, f) => ({ ...acc, [f.key]: f.label }), {} as Record<string, string>);

/** Common visit-title suggestions per family, offered via a <datalist> on the appointment title field - a suggestion, not a constraint; the field stays free text. */
const FAMILY_VISIT_TITLES: Record<string, string[]> = {
    cardiology: ['Cardiology Consultation', 'Post-MI Follow-up', 'Hypertension Review', 'ECG Review'],
    obgyn: ['Antenatal Visit', 'Postnatal Check-up', 'Gynaecological Consultation', 'Family Planning Consultation'],
    dental: ['Orthodontic Adjustment', 'Dental Consultation', 'Oral Surgery Follow-up'],
    psychiatry: ['Initial Psychiatric Assessment', 'Medication Review', 'Therapy Follow-up'],
    dermatology: ['Skin Consultation', 'Lesion Review', 'Cosmetic Consultation'],
    ophthalmology: ['Eye Examination', 'Post-op Follow-up', 'Vision Check'],
    ent: ['ENT Consultation', 'Hearing Assessment Follow-up'],
    orthopedics: ['Orthopedic Consultation', 'Post-surgical Follow-up', 'Sports Injury Review'],
    endocrine: ['Diabetes Review', 'Endocrine Consultation', 'Thyroid Follow-up'],
    nephrology: ['Renal Function Review', 'Dialysis Follow-up'],
    gastro: ['GI Consultation', 'Liver Function Follow-up'],
    oncology: ['Oncology Consultation', 'Chemotherapy Review', 'Treatment Follow-up'],
    neurology: ['Neurology Consultation', 'Headache Review', 'Post-stroke Follow-up'],
    urology: ['Urology Consultation', 'PSA Follow-up'],
    neonatal: ['Newborn Check-up', 'NICU Follow-up'],
    anesthesia_pain: ['Pre-operative Assessment', 'Pain Management Review'],
    emergency: ['Urgent Consultation', 'Triage Follow-up'],
};

export const getSpecialtyVisitTitles = (specialization: string | undefined | null): string[] => {
    if (!specialization) return [];
    const family = SPECIALIZATION_TO_FAMILY[specialization];
    return family ? FAMILY_VISIT_TITLES[family] : [];
};
